import { useEffect, useId, useRef, useState } from "react"
import {
  DOG_BREED_CATALOG,
  MIXED_BREED,
  UNKNOWN_BREED,
  displayBreedValue,
  filterDogBreeds,
  resolveBreedSelection,
  type DogBreedEntry,
} from "~/data/dogBreeds"

interface SearchableBreedSelectorProps {
  id?: string
  label?: string
  value: string
  mixDetail?: string
  onChange: (breed: string, mixDetail?: string) => void
  onValidityChange?: (valid: boolean) => void
  required?: boolean
  placeholder?: string
}

export function SearchableBreedSelector({
  id,
  label = "Breed",
  value,
  mixDetail = "",
  onChange,
  onValidityChange,
  required = false,
  placeholder = "Search breeds…",
}: SearchableBreedSelectorProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const listboxId = `${inputId}-listbox`
  const [query, setQuery] = useState(displayBreedValue(value, mixDetail))
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [unmatched, setUnmatched] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const options = unmatched
    ? DOG_BREED_CATALOG.filter(
        (entry) => entry.name === MIXED_BREED || entry.name === UNKNOWN_BREED,
      )
    : filterDogBreeds(query)

  useEffect(() => {
    setQuery(displayBreedValue(value, mixDetail))
  }, [value, mixDetail])

  useEffect(() => {
    onValidityChange?.(!unmatched)
  }, [unmatched, onValidityChange])

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointerDown)
    return () => document.removeEventListener("mousedown", handlePointerDown)
  }, [])

  function applySelection(entry: DogBreedEntry) {
    const resolved = resolveBreedSelection(entry.name)
    if (!resolved) return
    onChange(resolved.breed, resolved.mixDetail)
    setQuery(displayBreedValue(resolved.breed, resolved.mixDetail))
    setUnmatched(false)
    setOpen(false)
  }

  function commitQuery() {
    const resolved = resolveBreedSelection(query)
    if (resolved) {
      onChange(resolved.breed, resolved.mixDetail)
      setQuery(displayBreedValue(resolved.breed, resolved.mixDetail))
      setUnmatched(false)
      setOpen(false)
      return
    }
    if (query.trim()) {
      setUnmatched(true)
      setOpen(true)
      return
    }
    onChange("", "")
    setUnmatched(false)
    setOpen(false)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true)
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlightIndex((index) => Math.min(index + 1, options.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlightIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const selected = options[highlightIndex]
      if (selected) applySelection(selected)
      else commitQuery()
    } else if (event.key === "Escape") {
      setOpen(false)
      setQuery(displayBreedValue(value, mixDetail))
      setUnmatched(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-[var(--color-text-primary)]">
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-invalid={unmatched}
        autoComplete="off"
        value={query}
        required={required}
        placeholder={placeholder}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
          setHighlightIndex(0)
          setUnmatched(false)
          if (!event.target.value.trim()) onChange("", "")
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          window.setTimeout(commitQuery, 120)
        }}
        onKeyDown={handleKeyDown}
        className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)]"
      />
      {unmatched && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          That isn’t in the breed list. Choose Mixed breed / crossbreed or Unknown / not sure.
        </p>
      )}
      {open && options.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] py-1 shadow-lg"
        >
          {options.map((entry, index) => (
            <li key={entry.id} role="option" aria-selected={entry.name === value}>
              <button
                type="button"
                className={`block w-full px-3 py-2 text-left text-sm ${
                  index === highlightIndex || entry.name === value
                    ? "bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                    : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-primary)]"
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applySelection(entry)}
              >
                {entry.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {value === MIXED_BREED && (
        <label className="mt-3 block text-sm font-medium text-[var(--color-text-primary)]">
          Mix detail (optional)
          <input
            type="text"
            value={mixDetail}
            placeholder="For example, Goldendoodle"
            onChange={(event) => onChange(MIXED_BREED, event.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)]"
          />
        </label>
      )}
    </div>
  )
}
